/**
 * TiTiler ColorMaps API (application package).
 * @see https://developmentseed.org/titiler/endpoints/colormaps/
 */
/** @type {Map<string, Record<string, unknown> | null>} */
const colormapDefCache = new Map()

function cacheKey(base, id) {
  return `${base.replace(/\/$/, '')}::${id}`
}

/**
 * Métadonnées RGBA du colormap (indices 0–255).
 * @param {string} titilerBase
 * @param {string} colorMapId
 * @returns {Promise<Record<string, unknown> | null>}
 */
export async function fetchTitilerColorMapDefinition(titilerBase, colorMapId) {
  const base = (titilerBase || '').trim().replace(/\/$/, '')
  if (!base || !colorMapId) return null
  const key = cacheKey(base, colorMapId)
  if (colormapDefCache.has(key)) {
    return colormapDefCache.get(key)
  }
  const url = `${base}/colorMaps/${encodeURIComponent(colorMapId)}`
  const res = await fetch(url)
  if (!res.ok) {
    colormapDefCache.set(key, null)
    return null
  }
  const ct = (res.headers.get('content-type') || '').toLowerCase()
  if (!ct.includes('json')) {
    colormapDefCache.set(key, null)
    return null
  }
  const data = await res.json()
  colormapDefCache.set(key, data)
  return data
}

/**
 * converts TiTiler color map definition to CSS gradient
 * @param {Record<string, unknown> | null} def
 * @returns {string | null}
 */
export function colormapDefinitionToCssGradient(def) {
  if (!def || typeof def !== 'object') return null
  const SAMPLE = [0, 32, 64, 96, 128, 160, 192, 224, 255]
  const parts = []
  for (const i of SAMPLE) {
    const rgba = def[String(i)]
    if (!Array.isArray(rgba) || rgba.length < 3) continue
    const r = rgba[0]
    const g = rgba[1]
    const b = rgba[2]
    const a = rgba[3] != null ? rgba[3] : 255
    const pct = (i / 255) * 100
    parts.push(`rgba(${r},${g},${b},${a / 255}) ${pct.toFixed(2)}%`)
  }
  if (parts.length < 2) return null
  return `linear-gradient(to right, ${parts.join(', ')})`
}

/**
 * @param {string} titilerBase —
 * @returns {Promise<string[]>}
 */
export async function fetchTitilerColorMapList(titilerBase) {
  const base = (titilerBase || '').trim().replace(/\/$/, '')
  if (!base) {
    return []
  }
  const url = `${base}/colorMaps`
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`TiTiler colorMaps: ${res.status}`)
  }
  const data = await res.json()
  let list = []

  if (Array.isArray(data)) {
    list = data
  } else if (data && typeof data === 'object') {
    // TiTiler 2.x : { colormaps: [ { id, links }, ... ] }
    const ogcStyle = data.colormaps ?? data.colorMaps
    if (Array.isArray(ogcStyle)) {
      list = ogcStyle.map(entry => {
        if (typeof entry === 'string') return entry
        if (entry && typeof entry.id === 'string') return entry.id
        return null
      })
    }
  }

  return [...list]
    .filter(id => typeof id === 'string' && id.length > 0)
    .sort((a, b) => a.localeCompare(b))
}

export function getTitilerColorMapImageUrl(titilerBase, colorMapId) {
  const base = (titilerBase || '').trim().replace(/\/$/, '')
  const id = encodeURIComponent(colorMapId)
  return `${base}/colorMaps/${id}?format=png`
}
